<template>
  <div>
    <h3 v-if="!isEdit" class="title is-3">Create new activity</h3>
    <h3 v-if="isEdit" class="title is-3">Edit this activity</h3>

    <form>
      <b-field horizontal label="Name:">
        <b-input
            v-model="name"
            :has-counter="true"
            maxlength="14"
            placeholder="Activity name (14 cars max)"
        >
        </b-input>
      </b-field>

      <b-field horizontal label="Pick one type:">
        <b-radio-button v-for="(typAct, index) in activityTypesArray"
                        :key="index"
                        v-model="activityType"
                        :expanded="false"
                        :native-value="typAct"
                        style="padding-right: 4px"
                        type="is-success"
        >
          <b-icon icon="check"></b-icon>
          <span>{{ typAct }}</span>
        </b-radio-button>
      </b-field>

      <b-field v-if="activityType" horizontal label="Content:">
        <!--------------- UNLOCK_ONLY --------------->
        <div v-if="activityType === activityTypes.UNLOCK_ONLY" class="container" style="background-color: #eaeaea">
          <b-field horizontal label="Text" style="padding: 5px">
            <b-input v-model="question" expanded placeholder="Text to display when unlocked"></b-input>
          </b-field>
        </div>
        <!--------------- UNLOCK_ONLY --------------->

        <!--------------- PHOTO --------------->
        <div v-if="activityType === activityTypes.PHOTO" class="container" style="background-color: #eaeaea">
          <b-field horizontal label="Text" style="padding: 5px">
            <b-input v-model="question" expanded placeholder="Text"></b-input>
          </b-field>
          <b-field horizontal label="Camera mode" style="padding: 5px">
            <b-checkbox-button v-for="(photoType, i) in photoActivityTypes"
                               :key="i"
                               v-model="photoActivityType"
                               :disabled="photoType !== 'Photo'"
                               :native-value="photoType"
                               type="is-info">
              <b-icon icon="camera"></b-icon>
              <span>{{ photoType }}</span>
            </b-checkbox-button>
          </b-field>
          <b-field horizontal label="Unlocked by default" style="padding: 5px">
            <b-switch v-model="unlocked_forced" :rounded="false" type="is-danger">
            </b-switch>
          </b-field>
        </div>
        <!--------------- PHOTO --------------->

        <!--------------- QUIZ --------------->
        <div v-if="activityType === activityTypes.QUIZ" class="container" style="background-color: #eaeaea">
          <b-field horizontal label="Question" style="padding: 5px">
            <b-input v-model="question" expanded placeholder="Your question"></b-input>
          </b-field>
          <b-field horizontal label="Unlocked by default" style="padding: 5px">
            <b-switch v-model="unlocked_forced" :rounded="false" type="is-danger">
            </b-switch>
          </b-field>
          <b-field horizontal label="Answers type" style="padding: 5px">
            <b-radio
                v-for="questionType of questionTypes" :key="questionType.code"
                v-model="type"
                :native-value="questionType.code"
                style="margin-top: -8px"
                type="is-link"
            >
              <div class="column field is-horizontal">
                {{ questionType.label }}
              </div>
            </b-radio>
          </b-field>
          <multi-choice-answers
              v-if="type === 'multiple-choices'"
              ref="multi_choices"
              style="padding: 0 5px 0px 5px"
              @updated="onMultiAnswersUpdate"
          ></multi-choice-answers>

          <single-answer
              v-if="type === 'free-text'"
              ref="single"
              :default-value="freeTextAnswer"
              style="padding: 0 5px 10px 5px"
              @updated="onFreeTextUpdate"
          ></single-answer>

          <b-field horizontal label="Explanation" style="padding: 5px">
            <b-input v-model="explanation" expanded placeholder="Explain the answer in a few words..."></b-input>
          </b-field>
        </div>
        <!--------------- QUIZ --------------->
      </b-field> <!-- CONTENT SPECIFIC -->

      <b-field horizontal label="Allowed attempts:">
        <b-slider
            v-model="allowedAttempts"
            :max="4"
            :min="1"
            :step="1"
            :tooltip="false"
        >
          <template v-for="val in [1, 2, 3]">
            <b-slider-tick :key="val" :value="val">{{ val }}</b-slider-tick>
          </template>
          <b-slider-tick :value="4">Unlimited</b-slider-tick>
        </b-slider>
      </b-field>

      <b-field horizontal label="Points:">
        <b-slider
            v-model="points"
            :max="500"
            :min="0"
            :step="100"
            :tooltip="true"
        >
          <template v-for="val in [0, 100, 200, 300, 400, 500]">
            <b-slider-tick :key="val" :value="val">{{ val }}</b-slider-tick>
          </template>
        </b-slider>
      </b-field>

      <b-field horizontal label="Time to answer:">
        <b-slider
            v-model="timeToAnswer"
            :max="40"
            :min="0"
            :step="10"
            :tooltip="true"
        >
          <b-slider-tick :value="0">Unlimited</b-slider-tick>
          <template v-for="val in [10, 20, 30, 40]">
            <b-slider-tick :key="val" :value="val">{{ val }}</b-slider-tick>
          </template>
        </b-slider>
      </b-field>

      <b-field horizontal label="Difficulty:">
        <b-rate
            v-model="difficulty"
            :custom-text="difficultyText"
            :max="3"
            icon="hexagon"
            spaced
        ></b-rate>
      </b-field>

      <b-field horizontal label="Tags:">
        <b-taginput
            v-model="tags"
            :allow-new="true"
            ellipsis
            icon="label"
            placeholder="Add a tag"
            type="is-warning">
        </b-taginput>
      </b-field>

      <b-field horizontal label="Image:">
        <div style="position: relative">
          <picture-input
              id="pictureInput"
              ref="challengePicture"
              :customStrings="fileUploadCustomTexts"
              :removable="true"
              buttonClass="button is-primary is-outlined"
              height="200"
              removeButtonClass="button is-outlined"
              width="250"
              @change="onPictureSelected"
          />
          <div v-if="imagePreviewUrl" id="injectedPreview" style="background-color: white">
            <img id="injectedPreviewImage" :src="imagePreviewUrl" alt="photo"/>
          </div>
          <button v-if="imagePreviewUrl" class="button is-info is-outlined"
                  style="width: 250px; margin-top: 5px;"
                  @click.prevent="hideImagePreview">Change image
          </button>
        </div>
      </b-field>
      <b-field horizontal label="The image is part of the question:">
        <b-switch v-model="imgPartOfTheQuestion"></b-switch>
      </b-field>

      <b-collapse :open="false" aria-id="contentIdForA11y1" style="padding-bottom: 40px">
        <a slot="trigger" slot-scope="props" aria-controls="contentIdForA11y1">
          <b-icon :icon="!props.open ? 'menu-down' : 'menu-up'"></b-icon>
          <i>{{ !props.open ? 'Show advanced fields' : 'Hide advanced fields' }}</i>
        </a>

        <div class="field is-horizontal">
          <div class="field-label is-normal">
            <label class="label">Order Number:</label>
          </div>
          <div class="field-body">
            <input v-model="orderNumber" :disabled="true" class="input"></input>
          </div>
        </div>

        <div class="field is-horizontal">
          <div class="field-label is-normal">
            <label class="label">Required Actions Before:</label>
          </div>
          <div class="field-body">
            <input v-model="requiredActionBefore" :disabled="true" class="input"></input>
          </div>
        </div>

        <div class="field is-horizontal">
          <div class="field-label is-normal">
            <label class="label">Activation Time:</label>
          </div>
          <div class="field-body">
            <input v-model="activationTime" :disabled="true" class="input"></input>
          </div>
        </div>

        <div class="field is-horizontal">
          <div class="field-label is-normal">
            <label class="label">On completion, do:</label>
          </div>
          <div class="field-body">
            <input v-model="onCompletionAction" :disabled="true" class="input"></input>
          </div>
        </div>

      </b-collapse>

      <div class="control submit">
        <button class="button is-light btn-margin" @click.prevent="cancel()">Back to activities</button>
        <button v-if="!isEdit" class="button is-primary" @click.prevent="createRawActivity">Create activity</button>
        <button v-if="isEdit" class="button is-primary" @click.prevent="updateRawActivity">Update activity</button>
      </div>
    </form>
    <b-loading :active.sync="isLoading" :is-full-page="true"></b-loading>
  </div>
</template>

<script>
import rawActivityService from '../../services/rawActivityService'
import displayHelper from '../../helpers/display-helper'
import PictureInput from 'vue-picture-input'
import MultiChoiceAnswers from './MultiChoiceAnswers'
import SingleAnswer from './SingleAnswer'
import activityTypesHelper from '../../enums/activityTypes'
import PHOTO_ACTIVITY_TYPES from '../../enums/photoActivityTypes'
import imageHelper from '../../helpers/image-helper'

export default {
  components: {
    PictureInput,
    MultiChoiceAnswers,
    SingleAnswer,
  },
  data() {
    return {

      // for update
      isEdit: false,
      rawActivityLoaded: null,
      rawActivityId: null,
      imagePreviewUrl: '',

      // normal
      rawActivities: [],
      name: '',
      activityType: '',
      content: '',
      imgSrc: '',
      imgPartOfTheQuestion: false,
      explanation: null,
      allowedAttempts: 4, // unlimited by default
      points: 100, // min points by default
      timeToAnswer: 0, // unlimited time to answer by default
      difficulty: 1, // easy by default
      orderNumber: 0,
      requiredActionBefore: '',
      activationTime: '',
      onCompletionAction: '',
      tags: [],
      activityTypes: [],
      activityTypesArray: [],
      isLoading: false,
      image: '',
      fileUploadCustomTexts: {
        upload: '<h1>Your device does not support file uploading.</h1>',
        drag: 'Drag file here or click here to upload'
      },

      // ------------ common to all contents ------------
      unlocked_forced: false,

      // ------------ specific QUIZ mode ------------
      question: '',
      freeTextAnswer: '',
      correctAnswer: null,
      answers: [{
        value: '',
      }, {
        value: '',
      }],
      type: '',
      questionTypes: [
        {
          code: 'free-text',
          label: 'Free text',
        },
        {
          code: 'multiple-choices',
          label: 'Multiple choices',
        }
      ],

      // ------------ specific PHOTO mode ------------
      photoActivityType: [],
      photoActivityTypes: [],
    }
  },
  computed: {
    difficultyText() {
      if (this.difficulty === 1) {
        return 'Easy'
      } else if (this.difficulty === 2) {
        return 'Medium'
      } else if (this.difficulty === 3) {
        return 'Hard'
      }
      return 'Unknown'
    },
  },
  watch: {
    'type': function (type) {
      if (type === 'free-text') {
        this.answers = [{
          value: '',
        }, {
          value: '',
        }]
        this.correctAnswer = ''
      } else {
        this.freeTextAnswer = ''
      }
    }
  },
  created() {
    this.activityTypes = activityTypesHelper.ACTIVITY_TYPES
    this.photoActivityTypes = PHOTO_ACTIVITY_TYPES
    this.activityTypesArray = _.filter(Object.values(activityTypesHelper.ACTIVITY_TYPES), (type) => {
      return !activityTypesHelper.disabledActivities(type)
    })
    try {
      if (this.$route.params.id) {
        this.isEdit = true
        this.rawActivityId = this.$route.params.id
        this.loadRawActivityById(this.rawActivityId)
      }
    } catch (err) {
      displayHelper.displayError(this, 'Oops, there was an error: ' + err.message)
    }
  },
  methods: {
    loadRawActivityById(rawActivityId) {
      this.isLoading = true
      var parent = this
      rawActivityService.getRawActivityById(rawActivityId)
          .then((rawActivity) => {
            console.log('Raw Activity received: ' + rawActivityId)
            console.dir(rawActivity)

            this.rawActivityLoaded = rawActivity

            this.name = this.rawActivityLoaded.name
            this.activityType = this.rawActivityLoaded.activityType
            this.content = this.rawActivityLoaded.content
            this.imagePreviewUrl = this.rawActivityLoaded.imgSrc
            this.imgPartOfTheQuestion = this.rawActivityLoaded.imgPartOfTheQuestion
            this.allowedAttempts = this.rawActivityLoaded.allowedAttempts
            this.points = this.rawActivityLoaded.points
            this.difficulty = this.rawActivityLoaded.difficulty
            this.timeToAnswer = this.rawActivityLoaded.timeToAnswer
            this.orderNumber = this.rawActivityLoaded.orderNumber
            this.requiredActionBefore = this.rawActivityLoaded.requiredActionBefore
            this.activationTime = this.rawActivityLoaded.activationTime
            this.onCompletionAction = this.rawActivityLoaded.onCompletionAction
            this.tags = this.rawActivityLoaded.tags
            this.unlocked_forced = this.rawActivityLoaded.content.unlocked_forced

            switch (this.activityType) {
              case (activityTypesHelper.ACTIVITY_TYPES.QUIZ):
                this.question = this.rawActivityLoaded.content.question
                this.explanation = this.rawActivityLoaded.content.explanation
                this.type = this.rawActivityLoaded.content.type === 'one-answer' ? 'multiple-choices' : 'free-text'
                if (this.rawActivityLoaded.content.type === 'free-text') {
                  this.freeTextAnswer = this.rawActivityLoaded.content.correctAnswer
                } else {
                  this.$nextTick(() => {
                    // hack because of the rendering makes it undefined before it's mounted
                    // see: https://forum.vuejs.org/t/solved-this-refs-key-returns-undefined-when-it-really-is/1226/19
                    this.$refs.multi_choices.init(this.rawActivityLoaded.content)
                  })
                }
                break
              case (activityTypesHelper.ACTIVITY_TYPES.PHOTO):
                this.question = this.rawActivityLoaded.content.question
                this.photoActivityType = this.rawActivityLoaded.content.photoActivityType
                this.type = 'picture'
                break
              case (activityTypesHelper.ACTIVITY_TYPES.UNLOCK_ONLY):
                this.question = this.rawActivityLoaded.content.question
                // force to be geo-locked
                this.unlocked_forced = false
                this.type = 'Unlock'
                break
              default:
                break
            }

            this.isLoading = false
          })
          .catch((err) => {
            displayHelper.displayError(parent, 'Oops, there was an error: ' + err.message)
            this.isLoading = false
          })
    },
    onPictureSelected(image) {
      if (image) {
        imageHelper.compressImg(this.$refs.challengePicture.image)
            .then((compressedChallengePic) => {
              this.image = compressedChallengePic
            })
      }
    },
    hideImagePreview() {
      this.imagePreviewUrl = ''
    },
    setCorrectAnswer() {
      if (this.type === 'free-text') {
        return this.freeTextAnswer
      } else {
        return this.correctAnswer + 1 + ''
      }
    },
    onMultiAnswersUpdate(payload) {
      this.answers = payload.answers
      this.correctAnswer = payload.correctAnswer
    },
    onFreeTextUpdate(freeTextAnswer) {
      this.freeTextAnswer = freeTextAnswer
    },
    transformAnswersForServer(answers) {
      const transformedAnswers = {}
      answers.forEach((answer, index) => {
        if (answer.value) {
          transformedAnswers[index + 1 + ''] = answer.value
        }
      })
      return transformedAnswers
    },
    setCompatibleAllowedAttempts(val) {
      // 4 is the value displayed on the slider for "Unlimited" but the rule for the player is '' = "Unlimited"
      if (val === 4) {
        return ''
      }
      return parseInt(val)
    },
    async createRawActivity() {
      this.isLoading = true
      console.log('new rawActivity')

      let contentToAdd = {}

      switch (this.activityType) {
        case (activityTypesHelper.ACTIVITY_TYPES.QUIZ):
          contentToAdd = {
            question: this.question,
            explanation: this.explanation,
            type: this.type === 'multiple-choices' ? 'one-answer' : 'free-text',
            correctAnswer: this.setCorrectAnswer(),
            answers: this.transformAnswersForServer(this.answers),
          }
          break
        case (activityTypesHelper.ACTIVITY_TYPES.PHOTO):
          contentToAdd = {
            question: this.question,
            photoActivityType: this.photoActivityType,
            type: 'picture',
          }
          break
        case (activityTypesHelper.ACTIVITY_TYPES.UNLOCK_ONLY):
          contentToAdd = {
            question: this.question,
            // force to be geo-locked
            unlocked_forced: false,
            type: 'Unlock'
          }
          break
        default:
          break
      }
      contentToAdd.unlocked_forced = this.unlocked_forced

      const rawActivity = {
        name: this.name,
        activityType: this.activityType,
        content: contentToAdd,
        allowedAttempts: this.setCompatibleAllowedAttempts(this.allowedAttempts),
        points: this.points,
        timeToAnswer: this.timeToAnswer,
        difficulty: this.difficulty,
        imgPartOfTheQuestion: this.imgPartOfTheQuestion,
        tags: this.tags
        // orderNumber: this.orderNumber,
        // requiredActionBefore:  this.requiredActionBefore,
        // activationTime:  this.activationTime,
        // onCompletionAction:  this.onCompletionAction,
      }

      const creationRawActivityResult = await rawActivityService.createRawActivity(rawActivity, this.image)
      this.isLoading = false
      if (creationRawActivityResult.isSuccess) {
        displayHelper.displaySuccess(this, 'The activity has successfully been created')
        this.$router.back()
      } else {
        displayHelper.displayError(this, 'Oops, there was an error: ' + creationRawActivityResult.errors)
      }
    },
    async updateRawActivity() {
      this.isLoading = true
      console.log('update rawActivity')

      let contentToAdd = {}

      switch (this.activityType) {
        case (activityTypesHelper.ACTIVITY_TYPES.QUIZ):
          contentToAdd = {
            question: this.question,
            explanation: this.explanation,
            type: this.type === 'multiple-choices' ? 'one-answer' : 'free-text',
            correctAnswer: this.setCorrectAnswer(),
            answers: this.transformAnswersForServer(this.answers),
          }
          break
        case (activityTypesHelper.ACTIVITY_TYPES.PHOTO):
          contentToAdd = {
            question: this.question,
            type: 'picture',
            photoActivityType: this.photoActivityType,
          }
          break
        case (activityTypesHelper.ACTIVITY_TYPES.UNLOCK_ONLY):
          contentToAdd = {
            question: this.question,
            // force to be geo-locked
            unlocked_forced: false,
            type: 'Unlock'
          }
          break
        default:
          break
      }
      contentToAdd.unlocked_forced = this.unlocked_forced

      const rawActivity = {
        rawActivityId: this.rawActivityId,
        name: this.name,
        activityType: this.activityType,
        content: contentToAdd,
        allowedAttempts: this.setCompatibleAllowedAttempts(this.allowedAttempts),
        points: this.points,
        timeToAnswer: this.timeToAnswer,
        difficulty: this.difficulty,
        imgPartOfTheQuestion: this.imgPartOfTheQuestion,
        tags: this.tags
        // orderNumber: this.orderNumber,
        // requiredActionBefore:  this.requiredActionBefore,
        // activationTime:  this.activationTime,
        // onCompletionAction:  this.onCompletionAction,
      }

      const updateRawActivityResult = await rawActivityService.updateRawActivity(rawActivity, this.image)
      this.isLoading = false
      if (updateRawActivityResult.isSuccess) {
        displayHelper.displaySuccess(this, 'The activity has successfully been updated')
        this.$router.back()
      } else {
        displayHelper.displayError(this, 'Oops, there was an error: ' + updateRawActivityResult.errors)
      }
    },
    cancel() {
      this.$router.go(-1)
    }
  }
}
</script>

<style scoped>
.btn-margin {
  margin-left: 10px;
  margin-right: 10px;
}

.picture-input /deep/ .preview-container {
  margin: 0;
  z-index: 1 !important;
}

.picture-input /deep/ .picture-preview {
  margin: 0;
  z-index: 1 !important;
}

.picture-input /deep/ .picture-inner {
  z-index: 1 !important;
}

.picture-input {
  text-align: left !important;
}

.picture-input /deep/ .preview-container {
  margin: 0;
}

#pictureInput, #injectedPreview {
  height: 200px;
  width: 250px;
  position: absolute;
}

#injectedPreviewImage {
  height: 200px;
  width: 250px;
}

#injectedPreview {
  top: 0;
  z-index: 10;
}
</style>
